<div
  class="flash-map-root"
  [class.is-dark-theme]="isDarkAppearanceTheme()"
  [class.is-presentation-active]="presentationState().active"
  [attr.data-appearance-theme]="appearanceTheme()"
>
  <div class="mode-content">
    <div
      class="mode-layer map-view"
      [class.is-active]="mode() === 'map' || !outlineEnabled()"
      [attr.aria-hidden]="mode() !== 'map' && outlineEnabled()"
      (pointerdown)="onMapViewPointerDown($event)"
    >
      <div #container class="flash-map-container" (dblclick)="onMapDoubleClick($event)"></div>
      @if (drawingEnabled()) {
        <svg class="flash-map-drawing-layer flash-map-drawing-layer--below" aria-hidden="true">
          <g [attr.transform]="drawingLayerBelowTransform()">
            @for (stroke of belowNodeVisualStrokes(); track stroke.id) {
              @switch (stroke.tool) {
                @case ('rectangle') {
                  <rect
                    [attr.fill]="stroke.fill ?? 'none'"
                    [attr.x]="drawingRectangle(stroke).x"
                    [attr.y]="drawingRectangle(stroke).y"
                    [attr.width]="drawingRectangle(stroke).width"
                    [attr.height]="drawingRectangle(stroke).height"
                    [attr.stroke]="stroke.color"
                    [attr.stroke-width]="stroke.lineWidth"
                    [attr.stroke-dasharray]="drawingStrokeDashArray(stroke)"
                    [attr.transform]="drawingRotationTransform(stroke)"
                    class="flash-map-drawing-object"
                    stroke-linejoin="round"
                  />
                }
                @case ('ellipse') {
                  <ellipse
                    [attr.fill]="stroke.fill ?? 'none'"
                    [attr.cx]="drawingEllipse(stroke).cx"
                    [attr.cy]="drawingEllipse(stroke).cy"
                    [attr.rx]="drawingEllipse(stroke).rx"
                    [attr.ry]="drawingEllipse(stroke).ry"
                    [attr.stroke]="stroke.color"
                    [attr.stroke-width]="stroke.lineWidth"
                    [attr.stroke-dasharray]="drawingStrokeDashArray(stroke)"
                    [attr.transform]="drawingRotationTransform(stroke)"
                    class="flash-map-drawing-object"
                  />
                }
              }
            }
          </g>
        </svg>
        <svg
          class="flash-map-drawing-layer"
          [class.is-drawing]="drawingTool() !== 'none'"
          [attr.aria-hidden]="drawingTool() === 'none'"
          (pointerdown)="onDrawingPointerDown($event)"
          (pointermove)="onDrawingPointerMove($event)"
          (pointerup)="onDrawingPointerEnd($event)"
          (pointercancel)="onDrawingPointerEnd($event)"
        >
          <defs>
            <marker
              id="flash-map-drawing-arrowhead"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
            </marker>
          </defs>
          <g [attr.transform]="drawingViewportTransform()">
            @for (stroke of drawingStrokes(); track stroke.id) {
              @switch (stroke.tool) {
                @case ('rectangle') {
                  <rect
                    [attr.data-drawing-id]="stroke.id"
                    fill="none"
                    stroke="transparent"
                    [attr.x]="drawingRectangle(stroke).x"
                    [attr.y]="drawingRectangle(stroke).y"
                    [attr.width]="drawingRectangle(stroke).width"
                    [attr.height]="drawingRectangle(stroke).height"
                    [attr.stroke-width]="drawingHitStrokeWidth(stroke)"
                    [attr.transform]="drawingRotationTransform(stroke)"
                    class="flash-map-drawing-hit-area flash-map-drawing-outline-hit-area"
                  />
                  @if (shouldRenderDrawingVisualInActiveLayer(stroke)) {
                    <rect
                      [attr.data-drawing-id]="stroke.id"
                      [attr.fill]="stroke.fill ?? 'none'"
                      [attr.x]="drawingRectangle(stroke).x"
                      [attr.y]="drawingRectangle(stroke).y"
                      [attr.width]="drawingRectangle(stroke).width"
                      [attr.height]="drawingRectangle(stroke).height"
                      [attr.stroke]="stroke.color"
                      [attr.stroke-width]="stroke.lineWidth"
                      [attr.stroke-dasharray]="drawingStrokeDashArray(stroke)"
                      [attr.transform]="drawingRotationTransform(stroke)"
                      class="flash-map-drawing-object"
                      stroke-linejoin="round"
                    />
                  }
                }
                @case ('ellipse') {
                  <ellipse
                    [attr.data-drawing-id]="stroke.id"
                    fill="none"
                    stroke="transparent"
                    [attr.cx]="drawingEllipse(stroke).cx"
                    [attr.cy]="drawingEllipse(stroke).cy"
                    [attr.rx]="drawingEllipse(stroke).rx"
                    [attr.ry]="drawingEllipse(stroke).ry"
                    [attr.stroke-width]="drawingHitStrokeWidth(stroke)"
                    [attr.transform]="drawingRotationTransform(stroke)"
                    class="flash-map-drawing-hit-area flash-map-drawing-outline-hit-area"
                  />
                  @if (shouldRenderDrawingVisualInActiveLayer(stroke)) {
                    <ellipse
                      [attr.data-drawing-id]="stroke.id"
                      [attr.fill]="stroke.fill ?? 'none'"
                      [attr.cx]="drawingEllipse(stroke).cx"
                      [attr.cy]="drawingEllipse(stroke).cy"
                      [attr.rx]="drawingEllipse(stroke).rx"
                      [attr.ry]="drawingEllipse(stroke).ry"
                      [attr.stroke]="stroke.color"
                      [attr.stroke-width]="stroke.lineWidth"
                      [attr.stroke-dasharray]="drawingStrokeDashArray(stroke)"
                      [attr.transform]="drawingRotationTransform(stroke)"
                      class="flash-map-drawing-object"
                    />
                  }
                }
                @case ('arrow') {
                  <line
                    [attr.data-drawing-id]="stroke.id"
                    [attr.x1]="drawingStartPoint(stroke).x"
                    [attr.y1]="drawingStartPoint(stroke).y"
                    [attr.x2]="drawingEndPoint(stroke).x"
                    [attr.y2]="drawingEndPoint(stroke).y"
                    stroke="transparent"
                    [attr.stroke-width]="drawingHitStrokeWidth(stroke)"
                    class="flash-map-drawing-hit-area"
                  />
                  <line
                    [attr.data-drawing-id]="stroke.id"
                    [attr.x1]="drawingStartPoint(stroke).x"
                    [attr.y1]="drawingStartPoint(stroke).y"
                    [attr.x2]="drawingEndPoint(stroke).x"
                    [attr.y2]="drawingEndPoint(stroke).y"
                    [attr.stroke]="stroke.color"
                    [attr.stroke-width]="stroke.lineWidth"
                    [attr.stroke-dasharray]="drawingStrokeDashArray(stroke)"
                    marker-end="url(#flash-map-drawing-arrowhead)"
                    stroke-linecap="round"
                    class="flash-map-drawing-object"
                  />
                }
                @case ('laser') {
                  <polyline
                    fill="none"
                    [attr.points]="drawingStrokePoints(stroke)"
                    [attr.stroke]="stroke.color"
                    [attr.stroke-width]="drawingLaserGlowWidth(stroke)"
                    [attr.stroke-dasharray]="drawingStrokeLength(stroke) + ' ' + drawingStrokeLength(stroke)"
                    [attr.stroke-dashoffset]="stroke.isFading ? -drawingStrokeLength(stroke) : 0"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="flash-map-laser-glow"
                    [class.is-fading]="stroke.isFading"
                  />
                  <polyline
                    [attr.data-drawing-id]="stroke.id"
                    fill="none"
                    [attr.points]="drawingStrokePoints(stroke)"
                    [attr.stroke]="stroke.color"
                    [attr.stroke-width]="stroke.lineWidth"
                    [attr.stroke-dasharray]="drawingStrokeLength(stroke) + ' ' + drawingStrokeLength(stroke)"
                    [attr.stroke-dashoffset]="stroke.isFading ? -drawingStrokeLength(stroke) : 0"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="flash-map-drawing-object flash-map-laser-stroke"
                    [class.is-fading]="stroke.isFading"
                  />
                }
                @default {
                  <polyline
                    [attr.data-drawing-id]="stroke.id"
                    fill="none"
                    [attr.points]="drawingStrokePoints(stroke)"
                    stroke="transparent"
                    [attr.stroke-width]="drawingHitStrokeWidth(stroke)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="flash-map-drawing-hit-area"
                  />
                  <polyline
                    [attr.data-drawing-id]="stroke.id"
                    fill="none"
                    [attr.points]="drawingStrokePoints(stroke)"
                    [attr.stroke]="stroke.color"
                    [attr.stroke-width]="stroke.lineWidth"
                    [attr.stroke-dasharray]="drawingStrokeDashArray(stroke)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="flash-map-drawing-object"
                  />
                }
              }
            }
            @if (selectedDrawing(); as selected) {
              @if (selected.tool === 'rectangle' || selected.tool === 'ellipse') {
                <g
                  [attr.data-drawing-id]="selected.id"
                  [attr.transform]="drawingRotationTransform(selected)"
                  class="flash-map-drawing-selection"
                >
                  <rect
                    fill="none"
                    [attr.x]="selectedDrawingBounds()?.x"
                    [attr.y]="selectedDrawingBounds()?.y"
                    [attr.width]="selectedDrawingBounds()?.width"
                    [attr.height]="selectedDrawingBounds()?.height"
                  />
                  @for (handle of ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']; track handle) {
                    <circle
                      [attr.data-drawing-id]="selected.id"
                      [attr.data-drawing-handle]="handle"
                      [attr.cx]="
                        handle.includes('w')
                          ? selectedDrawingBounds()!.x
                          : handle.includes('e')
                            ? selectedDrawingBounds()!.x + selectedDrawingBounds()!.width
                            : selectedDrawingBounds()!.x + selectedDrawingBounds()!.width / 2
                      "
                      [attr.cy]="
                        handle.includes('n')
                          ? selectedDrawingBounds()!.y
                          : handle.includes('s')
                            ? selectedDrawingBounds()!.y + selectedDrawingBounds()!.height
                            : selectedDrawingBounds()!.y + selectedDrawingBounds()!.height / 2
                      "
                      [attr.r]="drawingHandleRadius()"
                    />
                  }
                  <line
                    [attr.x1]="selectedDrawingBounds()!.x + selectedDrawingBounds()!.width / 2"
                    [attr.y1]="selectedDrawingBounds()!.y"
                    [attr.x2]="selectedDrawingBounds()!.x + selectedDrawingBounds()!.width / 2"
                    [attr.y2]="selectedDrawingBounds()!.y - drawingHandleRadius() * 4"
                  />
                  <circle
                    [attr.data-drawing-id]="selected.id"
                    data-drawing-handle="rotate"
                    [attr.cx]="selectedDrawingBounds()!.x + selectedDrawingBounds()!.width / 2"
                    [attr.cy]="selectedDrawingBounds()!.y - drawingHandleRadius() * 4"
                    [attr.r]="drawingHandleRadius()"
                  />
                </g>
              } @else if (selected.tool === 'arrow') {
                <g class="flash-map-drawing-selection">
                  @for (end of ['arrow-start', 'arrow-end']; track end) {
                    <circle
                      [attr.data-drawing-id]="selected.id"
                      [attr.data-drawing-handle]="end"
                      [attr.cx]="
                        end === 'arrow-start'
                          ? drawingStartPoint(selected).x
                          : drawingEndPoint(selected).x
                      "
                      [attr.cy]="
                        end === 'arrow-start'
                          ? drawingStartPoint(selected).y
                          : drawingEndPoint(selected).y
                      "
                      [attr.r]="drawingArrowEndHitRadius()"
                      class="flash-map-drawing-arrow-end-hit-area"
                    />
                    <circle
                      [attr.data-drawing-id]="selected.id"
                      [attr.data-drawing-handle]="end"
                      [attr.cx]="
                        end === 'arrow-start'
                          ? drawingStartPoint(selected).x
                          : drawingEndPoint(selected).x
                      "
                      [attr.cy]="
                        end === 'arrow-start'
                          ? drawingStartPoint(selected).y
                          : drawingEndPoint(selected).y
                      "
                      [attr.r]="drawingHandleRadius()"
                    />
                  }
                </g>
              }
            }
          </g>
        </svg>
        @if (selectedArrangeableDrawing()) {
          @if (selectedDrawingArrangePanelStyle(); as panelStyle) {
            <div
              class="flash-map-drawing-arrange-panel"
              role="toolbar"
              [attr.aria-label]="translations().drawing.arrangeToolbarAriaLabel"
              [style.left]="panelStyle.left"
              [style.top]="panelStyle.top"
              [style.transform]="panelStyle.transform"
            >
              <button
                type="button"
                class="flash-map-drawing-arrange-button"
                [disabled]="!canSendSelectedDrawingBackward()"
                [attr.aria-label]="translations().drawing.sendBackwardAriaLabel"
                [title]="translations().drawing.sendBackwardTitle"
                (click)="sendSelectedDrawingBackward()"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M7 12h10m-6-4-4 4 4 4" />
                </svg>
              </button>
              <button
                type="button"
                class="flash-map-drawing-arrange-button"
                [disabled]="!canBringSelectedDrawingForward()"
                [attr.aria-label]="translations().drawing.bringForwardAriaLabel"
                [title]="translations().drawing.bringForwardTitle"
                (click)="bringSelectedDrawingForward()"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M7 12h10m-4-4 4 4-4 4" />
                </svg>
              </button>
            </div>
          }
        }
      }
    </div>

    <div
      class="mode-layer outline-view"
      [class.is-active]="mode() === 'outline' && outlineEnabled()"
      [attr.aria-hidden]="mode() !== 'outline'"
    >
      <lib-outline-tree-view
        [data]="outlineTreeNodes()"
        [selectedNodeId]="selectedOutlineLine()"
        [editNodeId]="editingOutlineNodeId()"
        [error]="outlineError()"
        [translations]="translations().outline"
        [disabled]="!editable()"
        [active]="mode() === 'outline' && outlineEnabled()"
        (nodeSelect)="onOutlineNodeSelect($event)"
        (nodeEdit)="onOutlineNodeEdit($event)"
        (nodeCreate)="onOutlineNodeCreate($event)"
        (nodeDelete)="onOutlineNodeDelete($event)"
        (nodeIndent)="onOutlineNodeIndent($event)"
        (nodeOutdent)="onOutlineNodeOutdent($event)"
        (editFinished)="onOutlineEditFinished()"
      />
    </div>
  </div>

  @if (drawingEnabled() && mode() === 'map' && !presentationState().active) {
    <div
      class="flash-map-drawing-panel"
      [class.is-expanded]="drawingPanelVisible()"
      role="group"
      [attr.aria-label]="translations().drawing.panelAriaLabel"
      (mouseenter)="onDrawingPanelMouseEnter()"
      (mouseleave)="onDrawingPanelMouseLeave()"
    >
      <button
        type="button"
        class="flash-map-drawing-button"
        [class.is-active]="drawingTool() === 'pen'"
        [attr.aria-expanded]="drawingPanelVisible()"
        [attr.aria-label]="translations().drawing.penAriaLabel"
        [title]="translations().drawing.penTitle"
        (click)="onPenButtonClick()"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            d="m14.6 4.2 5.2 5.2M4 20l3.6-.8L19 7.8a1.8 1.8 0 0 0 0-2.6l-.2-.2a1.8 1.8 0 0 0-2.6 0L4.8 16.4 4 20Z"
          />
        </svg>
      </button>
      <div class="flash-map-drawing-tools">
        <button
          type="button"
          class="flash-map-drawing-button"
          [class.is-active]="drawingTool() === 'laser'"
          [title]="translations().drawing.laserTitle"
          [attr.aria-label]="translations().drawing.laserAriaLabel"
          (click)="toggleDrawingTool('laser')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 17 17 4m-8.5 1.5 1 1m2.5 2.5 1 1M4 20h16" />
          </svg>
        </button>
        <button
          type="button"
          class="flash-map-drawing-button"
          [class.is-active]="drawingTool() === 'rectangle'"
          [title]="translations().drawing.rectangleTitle"
          [attr.aria-label]="translations().drawing.rectangleAriaLabel"
          (click)="toggleDrawingTool('rectangle')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <rect x="4" y="6" width="16" height="12" rx="1" />
          </svg>
        </button>
        <button
          type="button"
          class="flash-map-drawing-button"
          [class.is-active]="drawingTool() === 'ellipse'"
          [title]="translations().drawing.ellipseTitle"
          [attr.aria-label]="translations().drawing.ellipseAriaLabel"
          (click)="toggleDrawingTool('ellipse')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <ellipse cx="12" cy="12" rx="8" ry="6" />
          </svg>
        </button>
        <button
          type="button"
          class="flash-map-drawing-button"
          [class.is-active]="drawingTool() === 'arrow'"
          [title]="translations().drawing.arrowTitle"
          [attr.aria-label]="translations().drawing.arrowAriaLabel"
          (click)="toggleDrawingTool('arrow')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h12m-4-4 4 4-4 4" />
          </svg>
        </button>
        <span class="flash-map-drawing-divider" aria-hidden="true"></span>
        <div class="flash-map-drawing-style">
          <button
            type="button"
            class="flash-map-drawing-style-button"
            [attr.aria-expanded]="drawingLineStyleMenuOpen()"
            [attr.aria-label]="translations().drawing.strokeStyleAriaLabel"
            [title]="translations().drawing.strokeStyleTitle"
            (click)="toggleDrawingLineStyleMenu()"
          >
            <span
              class="flash-map-drawing-style-preview"
              [class.is-dashed]="drawingLineStyle() === 'dashed'"
              aria-hidden="true"
            ></span>
          </button>
          @if (drawingLineStyleMenuOpen()) {
            <div
              class="flash-map-drawing-style-menu"
              role="menu"
              [attr.aria-label]="translations().drawing.strokeStyleAriaLabel"
            >
              @for (style of drawingLineStyles; track style) {
                <button
                  type="button"
                  role="menuitemradio"
                  class="flash-map-drawing-style-option"
                  [class.is-active]="drawingLineStyle() === style"
                  [attr.aria-checked]="drawingLineStyle() === style"
                  [attr.aria-label]="drawingLineStyleLabel(style)"
                  (click)="selectDrawingLineStyle(style)"
                >
                  <span
                    class="flash-map-drawing-style-preview"
                    [class.is-dashed]="style === 'dashed'"
                    aria-hidden="true"
                  ></span>
                </button>
              }
            </div>
          }
        </div>
        <div class="flash-map-drawing-color-control">
          <button
            type="button"
            class="flash-map-drawing-color-button"
            [class.is-active]="drawingColorMenu() === 'stroke'"
            [attr.aria-expanded]="drawingColorMenu() === 'stroke'"
            [attr.aria-label]="translations().drawing.strokeColorAriaLabel"
            [title]="translations().drawing.strokeColorTitle"
            (click)="toggleDrawingColorMenu('stroke')"
          >
            <span
              class="flash-map-drawing-color-preview flash-map-drawing-color-preview--stroke"
              [style.background-color]="drawingColor()"
            ></span>
          </button>
          @if (drawingColorMenu() === 'stroke') {
            <div
              class="flash-map-drawing-color-menu"
              role="menu"
              [attr.aria-label]="translations().drawing.strokeColorAriaLabel"
            >
              @for (color of drawingColors; track color) {
                <button
                  type="button"
                  role="menuitemradio"
                  class="flash-map-drawing-swatch"
                  [class.is-active]="drawingColor() === color"
                  [style.background-color]="color"
                  [attr.aria-checked]="drawingColor() === color"
                  [attr.aria-label]="color"
                  (click)="selectDrawingColor(color)"
                ></button>
              }
              <label
                class="flash-map-drawing-custom-color"
                [attr.aria-label]="translations().drawing.customColorAriaLabel"
              >
                <input
                  type="color"
                  [value]="drawingColor()"
                  [attr.aria-label]="translations().drawing.customColorAriaLabel"
                  (input)="onDrawingColorChange($event)"
                />
              </label>
            </div>
          }
        </div>
        <div class="flash-map-drawing-color-control">
          <button
            type="button"
            class="flash-map-drawing-color-button"
            [class.is-active]="drawingColorMenu() === 'fill'"
            [attr.aria-expanded]="drawingColorMenu() === 'fill'"
            [attr.aria-label]="translations().drawing.fillColorAriaLabel"
            [title]="translations().drawing.fillColorTitle"
            (click)="toggleDrawingColorMenu('fill')"
          >
            <span
              class="flash-map-drawing-color-preview"
              [class.is-empty]="drawingFillColor() === null"
              [style.background-color]="drawingFillColor()"
            ></span>
          </button>
          @if (drawingColorMenu() === 'fill') {
            <div
              class="flash-map-drawing-color-menu"
              role="menu"
              [attr.aria-label]="translations().drawing.fillColorAriaLabel"
            >
              <button
                type="button"
                role="menuitemradio"
                class="flash-map-drawing-no-fill"
                [class.is-active]="drawingFillColor() === null"
                [attr.aria-checked]="drawingFillColor() === null"
                [title]="translations().drawing.noFillTitle"
                [attr.aria-label]="translations().drawing.noFillTitle"
                (click)="selectDrawingFillColor(null)"
              ></button>
              @for (color of drawingColors; track color) {
                <button
                  type="button"
                  role="menuitemradio"
                  class="flash-map-drawing-swatch"
                  [class.is-active]="drawingFillColor() === color"
                  [style.background-color]="color"
                  [attr.aria-checked]="drawingFillColor() === color"
                  [attr.aria-label]="color"
                  (click)="selectDrawingFillColor(color)"
                ></button>
              }
              <label
                class="flash-map-drawing-custom-color"
                [attr.aria-label]="translations().drawing.customColorAriaLabel"
              >
                <input
                  type="color"
                  [value]="drawingFillColor() ?? '#ffffff'"
                  [attr.aria-label]="translations().drawing.customColorAriaLabel"
                  (input)="onDrawingFillColorChange($event)"
                />
              </label>
            </div>
          }
        </div>
        <span class="flash-map-drawing-divider" aria-hidden="true"></span>
        <div class="flash-map-drawing-width">
          <button
            type="button"
            class="flash-map-drawing-width-button"
            [attr.aria-expanded]="drawingStrokeWidthMenuOpen()"
            [attr.aria-label]="translations().drawing.strokeWidthAriaLabel"
            [title]="translations().drawing.strokeWidthTitle"
            (click)="toggleDrawingStrokeWidthMenu()"
          >
            <span
              class="flash-map-drawing-width-preview flash-map-drawing-width-preview--current"
              aria-hidden="true"
              [style.height.px]="drawingStrokeWidth()"
            ></span>
          </button>
          @if (drawingStrokeWidthMenuOpen()) {
            <div class="flash-map-drawing-width-menu" role="menu">
              @for (width of drawingStrokeWidths; track width) {
                <button
                  type="button"
                  role="menuitemradio"
                  class="flash-map-drawing-width-option"
                  [class.is-active]="drawingStrokeWidth() === width"
                  [attr.aria-checked]="drawingStrokeWidth() === width"
                  [attr.aria-label]="drawingStrokeWidthLabel(width)"
                  (click)="selectDrawingStrokeWidth(width)"
                >
                  <span
                    class="flash-map-drawing-width-preview"
                    aria-hidden="true"
                    [style.height.px]="width"
                  ></span>
                </button>
              }
            </div>
          }
        </div>
        <span class="flash-map-drawing-divider" aria-hidden="true"></span>
        <button
          type="button"
          class="flash-map-drawing-button"
          [disabled]="!hasPersistentDrawings()"
          [title]="translations().drawing.clearTitle"
          [attr.aria-label]="translations().drawing.clearAriaLabel"
          (click)="clearPersistentDrawings()"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 7h16m-10 4v5m4-5v5M9 7l1-3h4l1 3m-9 0 1 13h10l1-13" />
          </svg>
        </button>
      </div>
    </div>
  }

  @if (presentationState().active) {
    <div
      class="flash-map-presentation-panel"
      role="group"
      [attr.aria-label]="translations().presentation.panelAriaLabel"
    >
      <button
        type="button"
        class="flash-map-presentation-button"
        [disabled]="presentationState().currentIndex === 0"
        [attr.aria-label]="translations().presentation.previousAriaLabel"
        [title]="translations().presentation.previousAriaLabel"
        (click)="previousPresentationSlide()"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <use href="/assets/icons/sprite.svg#icon-chevron-left"></use>
        </svg>
      </button>

      <button
        type="button"
        class="flash-map-presentation-button"
        [disabled]="presentationState().currentIndex >= presentationState().total - 1"
        [attr.aria-label]="translations().presentation.nextAriaLabel"
        [title]="translations().presentation.nextAriaLabel"
        (click)="nextPresentationSlide()"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <use href="/assets/icons/sprite.svg#icon-chevron-right"></use>
        </svg>
      </button>

      <span class="flash-map-presentation-count" aria-live="polite">
        {{ presentationSlideLabel() }}
      </span>

      <span class="flash-map-presentation-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="flash-map-presentation-button"
        [attr.aria-label]="translations().presentation.closeAriaLabel"
        [title]="translations().presentation.closeAriaLabel"
        (click)="exitPresentation()"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <use href="/assets/icons/sprite.svg#icon-x"></use>
        </svg>
      </button>
    </div>
  }

  <lib-help-shortcuts
    [open]="helpOpen()"
    [title]="helpTitle()"
    [groups]="helpShortcuts()"
    [translations]="translations().help"
    (close)="onCloseHelp()"
  />
</div>
